// Action type constants used to classify a video's risk status from its action records.
//
// Dependency-light (no imports) so it can be re-exported from `@unbox/api` and consumed by the
// frontend — the review queue predicts the status client-side before the actions are saved, and
// has to agree with the server. Adding an action type here without updating `getVideoStatus`
// (videos/repo.ts) leaves the two out of step: that CASE expression is hardcoded SQL.

/** Actions that start work on a risk without closing it → `actionInProgress`. */
export const ACTION_IN_PROGRESS = ["contact_creator", "demonetization_ticket", "takedown_request", "trigger_sequence"];

/** Enforcement outcomes that close a risk → `riskActioned`. */
export const ACTION_COMPLETED = [
  "video_removed",
  "video_demonetized",
  "creator_removed",
  "remove_from_collaboration",
  "remove_from_ad_campaign",
  // Policy-specific resolve_risk outcomes (see resolveRiskActionType).
  "warning_sent",
  "removed_gmv_max",
  "warned_and_removed_gmv_max",
  "metadata_changed",
  "relationship_ended",
];

/** Outcomes that close a risk without enforcement → `noActionRequired`. */
export const ACTION_NO_ACTION = ["exception_made_no_action_required", "no_action_required", "risk_resolved"];

/**
 * Every action type that means a risk is already resolved, whether by enforcement or by an
 * explicit clearing decision. Deliberately excludes ACTION_IN_PROGRESS.
 */
export const RESOLVING_ACTION_TYPES = [...ACTION_COMPLETED, ...ACTION_NO_ACTION];

/** A person confirming the video has no risks, and undoing that. Logged for History; they don't act on a risk. */
export const NO_RISKS_CONFIRMED = "no_risks_confirmed";
export const NO_RISKS_CONFIRMATION_UNDONE = "no_risks_confirmation_undone";
export const MANUAL_REVIEW_ACTION_TYPES = [NO_RISKS_CONFIRMED, NO_RISKS_CONFIRMATION_UNDONE];

/** The safety statuses a person can confirm as having no risks. */
export const MANUALLY_CONFIRMABLE_STATUSES = ["noRisksFound", "unableToAnalyse", "videoPrivated"] as const;
